import { ReactNode, useCallback, useEffect, useMemo } from 'react';

import { EstimateContext } from './estimate.context';
import { EstimateContextType } from './estimate.types';
import { useDefineLoadEstimateCode, useEstimateLoader } from './hooks';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { pullLocalData, setLocalData } from '~/core/helpers/storageHelper';
import { useRouterTools } from '~/core/hooks/useRouterTools';
import { useAppSelector } from '~/redux/hooks';
import { selectIsSavingEstimate } from '~/redux/slices/estimate';
import {
  NotifierType,
  SavedNotification,
  showNotification,
} from '~legacy/components';

export const EstimateContextProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const isSavingEstimate = useAppSelector(selectIsSavingEstimate);
  const { getSearchParam } = useRouterTools();
  const loadEstimateCodeFromURL = getSearchParam(
    LOCAL_STORAGE_KEY.LOAD_ESTIMATE
  );
  const { loadEstimateCode, removeLoadEstimateCode } =
    useDefineLoadEstimateCode();
  const {
    fetchedEstimate,
    isFetchingEstimate,
    isFetchError,
    resetLoadedFlags,
  } = useEstimateLoader(loadEstimateCode);

  const startNewEstimate = useCallback(() => {
    const url = new URL(window.location.href);

    url.searchParams.delete(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);

    window.location.href = url.toString();
  }, []);

  const switchLoadedEstimateCode = useCallback(
    (code: string) => {
      if (code !== '' && code !== loadEstimateCode) {
        resetLoadedFlags();
        setLocalData(LOCAL_STORAGE_KEY.ESTIMATE_CODE_SWITCH, 'true');
        removeLoadEstimateCode();
        window.location.assign(
          `${window.location.origin}?${LOCAL_STORAGE_KEY.LOAD_ESTIMATE}=${code}`
        );
      }
    },
    [loadEstimateCode, removeLoadEstimateCode, resetLoadedFlags]
  );

  useEffect(() => {
    const loadEstimateCodeFromURL = new URL(
      window.location.href
    ).searchParams.get(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);

    if (!loadEstimateCodeFromURL) {
      const errorMsgs = pullLocalData<SavedNotification>(
        LOCAL_STORAGE_KEY.NOTIFICATIONS,
        (item) => item.notifierType === NotifierType.ERROR
      );

      errorMsgs.forEach((item) =>
        showNotification(
          item.notifierType,
          item.title,
          item.description,
          item.message,
          item.duration
        )
      );
    }
  }, [loadEstimateCodeFromURL]);

  const contextValue: EstimateContextType = useMemo(
    () => ({
      fetchedEstimate,
      isFetchError,
      isFetchingEstimate,
      isSavingEstimate,
      loadEstimateCode,
      startNewEstimate,
      removeLoadEstimateCode,
      switchLoadedEstimateCode,
      resetLoadedFlags,
    }),
    [
      fetchedEstimate,
      isFetchError,
      isFetchingEstimate,
      isSavingEstimate,
      loadEstimateCode,
      startNewEstimate,
      removeLoadEstimateCode,
      switchLoadedEstimateCode,
      resetLoadedFlags,
    ]
  );

  return (
    <EstimateContext.Provider value={contextValue}>
      {children}
    </EstimateContext.Provider>
  );
};
